import React from "react";
import { motion } from "framer-motion";
import ChoiceMagneticButton from "../shared/ChoiceMagneticButton.jsx";
import { scrollToId } from "../shared/utils.js";
import { ArrowRight } from "../shared/icons.jsx";

const MainHero = ({ onOpenCustomModal }) => {
  const heroStats = [
    { value: "9 sec", label: "Response" },
    { value: "72h", label: "Live" },
    { value: "0", label: "New Hires" },
  ];

  return (
    <section className="relative min-h-[70vh] flex items-center hero-mesh overflow-hidden pt-24 pb-8" data-testid="choice-hero">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-24 lg:py-32 text-center">
        <motion.div
          className="inline-flex items-center space-x-3 bg-white/[0.03] border border-teal-500/20 rounded-full px-4 py-2 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.6 }}
          data-testid="choice-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-teal-400 pulse-subtle" />
          <span className="badge-mono text-teal-400">DIGITAL EMPLOYEE · LIVE IN 72 HOURS</span>
        </motion.div>

        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[0.95] tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          data-testid="choice-headline"
        >
          Every inbound lead gets a reply in{" "}
          <span className="gradient-text">9 seconds.</span>
        </motion.h1>

        <motion.h2
          className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto font-body leading-relaxed mb-8 font-normal"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          data-testid="choice-subheadline"
        >
          As a result: up to <span className="text-teal-400 font-semibold">2x more qualified appointments</span> — same ad spend, no new hires.
        </motion.h2>

        <motion.div
          className="grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl mx-auto mb-8"
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          data-testid="choice-stats"
        >
          {heroStats.map((stat, index) => (
            <div
              key={stat.label}
              className="bento-card py-4 px-3 sm:px-5 border border-teal-500/20 bg-teal-500/[0.04]"
              data-testid={`choice-stat-${index}`}
            >
              <div className="text-2xl sm:text-3xl font-bold font-mono text-teal-400">{stat.value}</div>
              <div className="text-[10px] sm:text-xs font-mono text-gray-500 uppercase tracking-wide mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        <motion.p
          className="text-sm text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22, duration: 0.6 }}
          data-testid="hero-proof-line"
        >
          <span className="text-white font-semibold">Proof:</span>{" "}
          <a
            href="https://ozexteriors.com.au/"
            target="_blank"
            rel="noreferrer"
            className="text-white hover:text-amber-400 transition-colors"
          >
            Oz Exteriors
          </a>{" "}
          books{" "}
          <span className="text-amber-400 font-semibold">+6 additional appointments/week</span>{" "}
          after go-live —{" "}
          <button
            type="button"
            onClick={() => scrollToId("field-proof")}
            className="text-teal-400 hover:text-teal-300 transition-colors underline underline-offset-2"
          >
            see case study ↓
          </button>
        </motion.p>

        <motion.p
          className="text-sm sm:text-base text-white font-semibold mb-8 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          data-testid="choice-definition"
        >
          A digital employee for your team — not a replacement. It qualifies, logs, and books; your people close.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center max-w-lg mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <ChoiceMagneticButton
            className="bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500 px-6 py-3.5 rounded-2xl font-semibold text-base flex items-center justify-center space-x-2"
            onClick={() => scrollToId("plug-play")}
            data-testid="hero-cta-plug-play"
          >
            <span>Explore Plug&Play Agents</span>
            <ArrowRight />
          </ChoiceMagneticButton>
          <ChoiceMagneticButton
            primary={false}
            className="border border-purple-500/40 bg-purple-500/10 text-purple-300 hover:text-white px-6 py-3.5 rounded-2xl font-semibold text-base"
            onClick={onOpenCustomModal}
            data-testid="hero-cta-custom"
          >
            Request Custom Agent
          </ChoiceMagneticButton>
        </motion.div>

        <motion.p
          className="text-sm text-gray-500 font-mono mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.6 }}
        >
          Ready-made agents or build your own ↓
        </motion.p>
      </div>
    </section>
  );
};

export default MainHero;
